/**
 * One e-form visit's write queue.
 *
 * Render issues a visit token; the server keys every write (upload, submit) on
 * it and creates the visit's response on the first write, answering with a
 * refreshed token that names the response. So writes must run one at a time,
 * each with the token the previous one returned — two uploads fired together
 * would otherwise both arrive without a response ID and split the visit across
 * two responses.
 *
 * A failed write leaves the token as it was and does not block later writes.
 */
export interface EFormVisitQueue {
  /** Runs `write` after every earlier write has settled, with the latest token. */
  run<T>(write: (visitToken: string) => Promise<T>): Promise<T>
  /** The latest token ('' when render issued none). */
  readonly visitToken: string
}

export function createVisitQueue(initialToken: string | null | undefined): EFormVisitQueue {
  let visitToken = initialToken ?? ''
  let tail: Promise<unknown> = Promise.resolve()

  return {
    run<T>(write: (visitToken: string) => Promise<T>): Promise<T> {
      const next = tail.then(async () => {
        const result = await write(visitToken)
        const refreshed = (result as { visitToken?: unknown } | null | undefined)?.visitToken
        if (typeof refreshed === 'string' && refreshed !== '') {
          visitToken = refreshed
        }
        return result
      })
      // The chain continues whether this write succeeds or fails.
      tail = next.catch(() => undefined)
      return next
    },
    get visitToken() {
      return visitToken
    },
  }
}
